import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Modal} from 'antd';
import {Chip, Divider, IconButton, ListItemText, Menu, MenuItem, Tooltip} from '@mui/material';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import WhatshotOutlinedIcon from '@mui/icons-material/WhatshotOutlined';
import {error_manager} from '../../../../helper/error_manager';
import {SESSION_STATUS} from './seenAreaSession';
import {formatPercent} from './seenAreaScore';
import {getSeenAreaKey} from './holdToggle';

const buttonSx = {
    backgroundColor: 'rgba(255,255,255,0.95)',
    border: '2px solid #333',
    borderRadius: '6px',
    padding: '4px',
    '&:hover': {backgroundColor: 'rgba(230,230,230,0.95)'},
};

const levelLabel = ({label, kind, level}) => label > 0 ? `${label}×` : `${kind} ${level}`;

const ScoreSplit = ({score}) => (
    <div>
        {score.levels.map(row =>
            <div key={`${row.kind}:${row.level}`}>
                {levelLabel(row)}: {row.seen}/{row.total} ({formatPercent(row.fraction)})
            </div>)}
        <div style={{marginTop: 4}}>Average of the levels: {formatPercent(score.overall)}</div>
    </div>
);

/** Heatmap toggle and coverage chip next to the minimap toggle; recording runs without them (H9). */
const SeenAreaControls = ({controller, canView, isSuperuser, keyboardShortcuts}) => {
    const [snapshot, setSnapshot] = useState(() => controller?.getSnapshot());
    const [menuAnchor, setMenuAnchor] = useState(null);

    useEffect(() => (controller ? controller.subscribe(setSnapshot) : undefined), [controller]);

    if (!controller || !canView || snapshot?.status !== SESSION_STATUS.READY) return null;
    const {visible, score, hasTissue, viewingUser, users} = snapshot;
    const key = getSeenAreaKey(keyboardShortcuts);

    const openMenu = (event) => {
        setMenuAnchor(event.currentTarget);
        if (isSuperuser) controller.loadUsers().catch(error => error_manager('Error loading seen-area users', error));
    };

    const confirmReset = () => {
        setMenuAnchor(null);
        Modal.confirm({
            title: 'Reset your seen area?',
            content: 'The heatmap for this slide starts again from empty. The current one is kept on the server.',
            okText: 'Reset',
            cancelText: 'Cancel',
            onOk: () => controller.reset().catch(error => error_manager('Error resetting seen area', error)),
        });
    };

    const viewUser = (user) => {
        setMenuAnchor(null);
        controller.viewUser(user).catch(error => error_manager('Error loading seen area', error));
    };

    const chipLabel = hasTissue ? `Seen ${formatPercent(score?.overall)}` : 'Seen –';
    const chipTooltip = !hasTissue
        ? 'Coverage needs the stitch manifest; build it to see the percentage'
        : score?.levels.length ? <ScoreSplit score={score}/> : 'No tissue on the reachable levels';

    return (
        <>
            <Tooltip title={`${visible ? 'Hide' : 'Show'} seen area on the minimap (tap ${key}, hold to peek)`} placement="left">
                <IconButton onClick={controller.toggleVisible} size="small" sx={buttonSx}>
                    {visible
                        ? <WhatshotIcon sx={{fontSize: 18, color: '#00a832'}}/>
                        : <WhatshotOutlinedIcon sx={{fontSize: 18, color: '#333'}}/>}
                </IconButton>
            </Tooltip>
            <Tooltip title={chipTooltip} placement="bottom">
                <Chip
                    size="small"
                    label={viewingUser ? `${viewingUser.username}: ${chipLabel}` : chipLabel}
                    onClick={openMenu}
                    sx={{backgroundColor: 'rgba(255,255,255,0.95)', border: '2px solid #333', fontWeight: 600}}
                />
            </Tooltip>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                {isSuperuser && [
                    <MenuItem key="me" selected={!viewingUser} onClick={() => viewUser(null)}>
                        <ListItemText primary="Show my seen area"/>
                    </MenuItem>,
                    ...(users || []).map(user =>
                        <MenuItem key={user.seen_area_id} selected={viewingUser?.seen_area_id === user.seen_area_id}
                                  onClick={() => viewUser(user)}>
                            <ListItemText primary={user.username}/>
                        </MenuItem>),
                    <Divider key="divider"/>,
                ]}
                <MenuItem onClick={confirmReset}>
                    <ListItemText primary="Reset my seen area"/>
                </MenuItem>
            </Menu>
        </>
    );
};

const mapStateToProps = (state) => ({
    canView: !!state.osdViewerConfigReducer?.permissions?.can_view_seen_heatmap,
    isSuperuser: !!state.osdViewerConfigReducer?.permissions?.is_superuser,
    keyboardShortcuts: state.morpheusSettingReducer?.keyboard_shortcuts,
});

export default connect(mapStateToProps)(SeenAreaControls);
